import { resolvedLocationSchema } from '@/types/api/services/search';
import { SORT_OPTION, URL_FILTER_SLUG } from '@/types/state/search';
import { placePreviewSchema } from '@/types/state/shared';
import { z } from 'zod';
import { brandTemplateSchema, dealItemBaseSchema } from './shared';

// --- Schemas ---

export const dealSearchItemSchema = dealItemBaseSchema.extend({
  place: placePreviewSchema,
});

export const sistaminutenSearchItemSchema = placePreviewSchema.extend({
  lastMinuteDiscount: z.number().optional(),
  bookableServicesCountLabel: z.string().optional(),
  maxLastMinuteDiscountTarget0: z.number().optional(),
  maxLastMinuteDiscountTarget1: z.number().optional(),
  maxLastMinuteDiscountTarget2: z.number().optional(),
});

const brandDealSearchItemSchema = dealItemBaseSchema.extend({
  place: placePreviewSchema,
});

const searchParamsSchema = z.object({
  keyword: z.string().optional(),
  location: z
    .discriminatedUnion('type', [
      z.object({ type: z.literal('namedLocation'), name: z.string(), id: z.string() }),
      z.object({ type: z.literal('myLocation'), name: z.string(), lat: z.number(), lon: z.number() }),
    ])
    .optional(),
  sort: z.enum(SORT_OPTION).optional(),
  filters: z.array(z.enum(URL_FILTER_SLUG)).optional(),
});

const dealsSectionBaseSchema = z.object({
  id: z.string(),
  searchParams: searchParamsSchema,
  title: z.string(),
  subtitle: z.string().optional(),
  hasMore: z.boolean().optional(),
  layout: z.union([z.literal('slider'), z.literal('grid')]).default('slider'),
});

const dealsSearchResultsBaseSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  isLastPage: z.boolean(),
  resolvedLocation: resolvedLocationSchema.optional(),
});

const dealsPreviewSectionSchema = dealsSectionBaseSchema.extend({
  type: z.literal('deals'),
  items: z.array(dealSearchItemSchema),
});

const sistaminutenPreviewSectionSchema = dealsSectionBaseSchema.extend({
  type: z.literal('sistaminuten'),
  items: z.array(sistaminutenSearchItemSchema),
});

const brandDealsPreviewSectionSchema = dealsSectionBaseSchema.extend({
  type: z.literal('brand_deals'),
  template: brandTemplateSchema,
  items: z.array(brandDealSearchItemSchema),
});

export const dealsSearchPreviewSectionSchema = z.discriminatedUnion('type', [
  dealsPreviewSectionSchema,
  sistaminutenPreviewSectionSchema,
  brandDealsPreviewSectionSchema,
]);

export const dealsSearchResultEntrySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('organic'), items: z.array(dealSearchItemSchema) }),
  z.object({ type: z.literal('sponsored'), section: dealsSearchPreviewSectionSchema }),
]);

export const sistaminutenSearchResultEntrySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('organic'), items: z.array(sistaminutenSearchItemSchema) }),
  z.object({ type: z.literal('sponsored'), section: dealsSearchPreviewSectionSchema }),
]);

export const brandDealsSearchResultsEntrySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('organic'), items: z.array(brandDealSearchItemSchema) }),
  z.object({ type: z.literal('sponsored'), section: dealsSearchPreviewSectionSchema }),
]);

export const dealsSearchResultsSchema = dealsSearchResultsBaseSchema.extend({
  results: z.array(dealsSearchResultEntrySchema),
});

export const sistaminutenSearchResultsSchema = dealsSearchResultsBaseSchema.extend({
  results: z.array(sistaminutenSearchResultEntrySchema),
});

export const brandDealsSearchResultsSchema = dealsSearchResultsBaseSchema.extend({
  results: z.array(brandDealsSearchResultsEntrySchema),
});

export const brandDealsSearchResultsResponseSchema = brandDealsSearchResultsSchema.extend({
  template: brandTemplateSchema,
});

export const dealsSearchResultsResponseSchema = z.discriminatedUnion('type', [
  dealsSearchResultsSchema.extend({ type: z.literal('deals') }),
  sistaminutenSearchResultsSchema.extend({ type: z.literal('sistaminuten') }),
  brandDealsSearchResultsResponseSchema.extend({ type: z.literal('brand_deals') }),
]);

export const dealsSearchPreviewResponseSchema = z.object({
  sections: z.array(dealsSearchPreviewSectionSchema),
  resolvedLocation: resolvedLocationSchema.optional(),
});

// --- Types ---
export type DealSearchItem = z.infer<typeof dealSearchItemSchema>;
export type SistaminutenSearchItem = z.infer<typeof sistaminutenSearchItemSchema>;
export type DealsSearchPreviewResponse = z.infer<typeof dealsSearchPreviewResponseSchema>;
export type DealsSearchResultsResponse = z.infer<typeof dealsSearchResultsResponseSchema>;
export type DealsSearchResultEntry = z.infer<typeof dealsSearchResultEntrySchema>;
export type SistaminutenSearchResultEntry = z.infer<typeof sistaminutenSearchResultEntrySchema>;
